/*
 * LAOR Size Guide (snippets/laor-size-guide.liquid): the finder, its two tabs and the chart.
 *
 * "המידה הרגילה שלי": a band chip and a cup chip (or a tap on a chart cell) give the size where they meet.
 * "מדידה בסנטימטרים": the under-bust slider picks the nearest listed band, the difference to the bust
 * slider picks the cup (data-cups-cm), and the chart gives the size. Either way one answer card shows the
 * size with a button that announces `lsg:size` ({ size }) on the document, which Step 2's size box picks
 * up, and then closes the popup the guide sits in. The answer is outlined in the chart.
 */
(function () {
  'use strict';
  if (window.LaorSizeGuide) return;

  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function fill(template, values) {
    let out = String(template || '');
    Object.keys(values).forEach((key) => { out = out.split('[' + key + ']').join(String(values[key])); });
    return out;
  }

  class Guide {
    constructor(root) {
      this.root = root;
      this.mode = 'size';
      this.band = -1;
      this.cup = -1;
      this.touched = false;
      this.bands = String(root.dataset.bands || '').split(',').filter(Boolean).map((b) => parseInt(b, 10));
      this.cupEnds = String(root.dataset.cupsCm || '').split('|').map((c) => parseFloat(c)).filter((c) => !Number.isNaN(c));
      this.resultEl = root.querySelector('[data-lsg-result]');
      this.sizeEl = root.querySelector('[data-lsg-size]');
      this.fromEl = root.querySelector('[data-lsg-from]');
      this.applyEl = root.querySelector('[data-lsg-apply]');
      this.appliedEl = root.querySelector('[data-lsg-applied]');
      this.barEl = root.querySelector('[data-lsg-back-bar]');
      this.barTextEl = root.querySelector('[data-lsg-bar-text]');
      this.sent = '';
      this.ranges = {};
      root.querySelectorAll('[data-lsg-range]').forEach((r) => { this.ranges[r.dataset.lsgRange] = r; });

      root.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-lsg-tab]');
        const band = e.target.closest('[data-lsg-band]');
        const cup = e.target.closest('[data-lsg-cup]');
        const cell = e.target.closest('[data-lsg-cell]');
        if (tab) this.setMode(tab.dataset.lsgTab);
        else if (band) {
          this.band = parseInt(band.dataset.lsgBand, 10);
          this.applied = false;
          this.render();
          if (this.cup < 0) this.reveal(root.querySelector('[data-lsg-step="cup"]'));
        } else if (cup) {
          this.cup = parseInt(cup.dataset.lsgCup, 10);
          this.applied = false;
          this.render();
          if (this.band > -1) this.reveal(this.resultEl);
        } else if (cell) {
          this.mode = 'size';
          this.band = parseInt(cell.dataset.band, 10);
          this.cup = parseInt(cell.dataset.cup, 10);
          this.applied = false;
          this.render(); /* the bar at the bottom shows the size, so the chart she tapped stays in view */
        } else if (e.target.closest('[data-lsg-apply]')) this.apply();
      });
      root.addEventListener('input', (e) => {
        if (!e.target.matches('[data-lsg-range]')) return;
        this.touched = true;
        this.applied = false;
        this.render();
      });
      this.render();
    }

    reveal(el) {
      if (el) requestAnimationFrame(() => el.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' }));
    }

    setMode(mode) {
      if (mode === this.mode) return;
      this.mode = mode;
      this.applied = false;
      this.render();
    }

    /* cm → band and cup indexes */
    fromCm() {
      const under = parseInt(this.ranges.under.value, 10);
      const bust = parseInt(this.ranges.bust.value, 10);
      let band = 0;
      this.bands.forEach((b, i) => { if (Math.abs(b - under) < Math.abs(this.bands[band] - under)) band = i; });
      const diff = bust - under;
      let cup = this.cupEnds.findIndex((end) => diff <= end);
      if (cup < 0) cup = this.cupEnds.length - 1;
      return { band, cup, under, bust };
    }

    current() {
      if (this.mode === 'cm') {
        if (!this.touched || !this.ranges.under) return null;
        return this.fromCm();
      }
      if (this.band < 0 || this.cup < 0) return null;
      return { band: this.band, cup: this.cup };
    }

    sizeAt(band, cup) {
      const cell = this.root.querySelector('[data-lsg-cell][data-band="' + band + '"][data-cup="' + cup + '"]');
      return cell ? cell.dataset.size : '';
    }

    render() {
      const root = this.root;
      root.dataset.mode = this.mode;
      root.querySelectorAll('[data-lsg-tab]').forEach((t) => t.setAttribute('aria-selected', t.dataset.lsgTab === this.mode ? 'true' : 'false'));
      root.querySelectorAll('[data-lsg-band]').forEach((b) => b.setAttribute('aria-checked', parseInt(b.dataset.lsgBand, 10) === this.band ? 'true' : 'false'));
      root.querySelectorAll('[data-lsg-cup]').forEach((b) => b.setAttribute('aria-checked', parseInt(b.dataset.lsgCup, 10) === this.cup ? 'true' : 'false'));
      root.querySelectorAll('[data-lsg-step]').forEach((s) => {
        const done = s.dataset.lsgStep === 'band' ? this.band > -1 : this.cup > -1;
        s.classList.toggle('is-done', done);
      });
      Object.keys(this.ranges).forEach((k) => {
        const r = this.ranges[k];
        const out = root.querySelector('[data-lsg-out="' + k + '"]');
        if (out) out.textContent = r.value + ' ' + (r.dataset.unit || '');
        const pct = ((r.value - r.min) / (r.max - r.min)) * 100;
        r.style.setProperty('--lsg-pct', pct + '%');
      });

      const at = this.current();
      const size = at ? this.sizeAt(at.band, at.cup) : null;
      root.querySelectorAll('[data-lsg-col]').forEach((th) => th.classList.toggle('is-on', !!at && parseInt(th.dataset.lsgCol, 10) === at.band));
      root.querySelectorAll('[data-lsg-row]').forEach((tr) => tr.classList.toggle('is-on', !!at && parseInt(tr.dataset.lsgRow, 10) === at.cup));
      root.querySelectorAll('[data-lsg-cell]').forEach((c) => {
        const inBand = !!at && parseInt(c.dataset.band, 10) === at.band;
        const inCup = !!at && parseInt(c.dataset.cup, 10) === at.cup;
        c.classList.toggle('is-picked', inBand && inCup);
        c.classList.toggle('is-dim', !!at && !(inBand || inCup));
      });

      const state = !at ? 'idle' : (size ? 'found' : 'none');
      if (this.resultEl.dataset.state !== state || (state === 'found' && this.lastSize !== size)) {
        this.resultEl.dataset.state = state;
        if (state === 'found' && !reduceMotion) {
          this.resultEl.classList.remove('is-pop');
          void this.resultEl.offsetWidth;
          this.resultEl.classList.add('is-pop');
        }
      }
      this.lastSize = size;
      if (size) {
        this.sizeEl.textContent = size;
        if (this.mode === 'cm') this.fromEl.textContent = fill(root.dataset.fromCm, { under: at.under, bust: at.bust });
        else {
          const bandEu = this.bands[at.band];
          const cupEl = root.querySelector('[data-lsg-cup="' + at.cup + '"]');
          this.fromEl.textContent = fill(root.dataset.from, { size: bandEu + (cupEl ? cupEl.dataset.label : '') });
        }
        /* The size she found becomes Step 2's size straight away (unless Step 2 is set to a size per
           bra); the bar at the bottom says so and takes her back to the colours */
        if (this.sent !== size) {
          this.sent = size;
          document.dispatchEvent(new CustomEvent('lsg:size', { detail: { size, auto: true } }));
        }
        if (this.appliedEl) this.appliedEl.textContent = fill(root.dataset.applied, { size });
        if (this.barTextEl) this.barTextEl.textContent = fill(root.dataset.bar, { size });
        /* Opened from Step 2 the button leads back to the colours; anywhere else it just goes on */
        if (this.applyEl) this.applyEl.textContent = window.location.hash === '#step-2' ? root.dataset.apply : (root.dataset.applyAlt || root.dataset.apply);
      }
      if (this.barEl) this.barEl.hidden = !size;
    }

    apply() {
      const dialog = this.root.closest('.sugar-popup-dialog, [role="dialog"], dialog');
      const close = dialog && dialog.querySelector('[data-popup-close]');
      if (close) close.click();
    }
  }

  const done = new WeakSet();
  function initAll(scope) {
    (scope || document).querySelectorAll('[data-lsg]').forEach((el) => {
      if (!done.has(el)) { done.add(el); new Guide(el); }
    });
  }

  /* Back (Android button, iPhone swipe) closes only the size guide, never Step 2 under it: opening the
     guide adds a history entry, and a back while it is open just closes it. Closing it another way
     (X, the button, outside) takes that entry back out. */
  let pushed = false;
  let skipPop = false;
  function guideDialog() {
    const g = document.querySelector('[data-lsg]');
    return g && g.closest('.sugar-popup-dialog, dialog');
  }
  function watch() {
    const dialog = guideDialog();
    if (!dialog || dialog._lsgWatch) return;
    dialog._lsgWatch = true;
    const isOpen = () => dialog.classList.contains('is-open') || dialog.open === true;
    new MutationObserver(() => {
      if (isOpen()) {
        /* the bar's button names where it leads: back to the colours from Step 2, otherwise on */
        const g = dialog.querySelector('[data-lsg]');
        const btn = g && g.querySelector('[data-lsg-apply]');
        if (btn) btn.textContent = window.location.hash === '#step-2' ? g.dataset.apply : (g.dataset.applyAlt || g.dataset.apply);
      }
      if (isOpen() && !pushed) {
        pushed = true;
        history.pushState({ lsgGuide: true, prev: history.state }, '', window.location.href);
      } else if (!isOpen() && pushed) {
        pushed = false;
        if (history.state && history.state.lsgGuide) { skipPop = true; history.back(); }
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['open', 'class'] });
  }
  window.addEventListener('popstate', (e) => {
    if (skipPop) { skipPop = false; e.stopImmediatePropagation(); return; }
    const dialog = guideDialog();
    if (pushed && dialog && (dialog.classList.contains('is-open') || dialog.open)) {
      e.stopImmediatePropagation();
      pushed = false;
      const close = dialog.querySelector('[data-popup-close]');
      if (close) close.click(); else if (dialog.close) dialog.close();
    }
  }, true);

  window.LaorSizeGuide = { initAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { initAll(); watch(); });
  else { initAll(); watch(); }
  window.addEventListener('load', watch);
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
